import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { catchError, firstValueFrom } from 'rxjs';
import { StorageService } from './storage.service';
import { NetworkService } from './network.service';
import { LoggerService } from './logger.service';
import { ErrorHandlerService } from './error-handler.service';
import { ToastService } from './toast.service';
import { ApiRequest, ApiResponse } from '../models/api.models';
import { environment } from '../../environments/environment';

/**
 * Status of a queued request
 */
export type QueuedRequestStatus = 'pending' | 'processing' | 'failed' | 'completed';

/**
 * Metadata for a queued request (for UI feedback)
 */
export interface QueuedRequestMetadata {
  description?: string;  // User-friendly description
  page?: string;         // Page where request was created
}

/**
 * Serialized file data for FormData
 */
interface SerializedFile {
  type: 'file';
  name: string;
  data: string;  // Base64 encoded
  lastModified?: number;
}

/**
 * Serialized request data (can be ApiRequest object or serialized FormData)
 */
interface SerializedRequestData {
  isFormData: boolean;
  data: { [key: string]: string | number | boolean | SerializedFile };
  urlEncodedHeaders?: string;
}

/**
 * Queued request structure
 */
export interface QueuedRequest {
  id: string;                    // Unique ID (UUID)
  taskID: string;                 // API taskID (e.g. 'createJob', 'editUser')
  serializedData: SerializedRequestData;  // Serialized request data
  timestamp: number;              // Creation timestamp
  retryCount: number;             // Number of retry attempts
  status: QueuedRequestStatus;    // Current status
  error?: string;                 // Error message on failure
  metadata?: QueuedRequestMetadata;  // Additional metadata for UI
}

/**
 * Offline Queue Service
 * 
 * Manages a queue of POST requests that are stored when the app is offline
 * and automatically synchronized when the app comes back online.
 * 
 * Features:
 * - Persistent storage in IndexedDB (survives app restarts)
 * - Automatic synchronization on online status change
 * - Retry logic for failed requests
 * - Observable for queue status updates
 */
@Injectable({
  providedIn: 'root'
})
export class OfflineQueueService implements OnDestroy {
  private readonly storageService = inject(StorageService);
  private readonly networkService = inject(NetworkService);
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly toastService = inject(ToastService);
  private readonly baseUrl = environment.apiUrl;

  private readonly STORAGE_KEY = 'offlineQueue';
  private readonly MAX_QUEUE_SIZE = 50;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY_BASE = 1000; // Base delay in ms

  private readonly pendingCountSubject = new BehaviorSubject<number>(0);
  public readonly pendingCount$: Observable<number> = this.pendingCountSubject.asObservable();

  /** Emits the number of successfully sent requests after a queue run (only if > 0) */
  private readonly queueProcessedSubject = new Subject<number>();
  public readonly queueProcessed$: Observable<number> = this.queueProcessedSubject.asObservable();

  private queue: QueuedRequest[] = [];
  private isProcessing = false;
  private networkSubscription?: Subscription;
  private initialized = false;

  // TaskIDs that should be queued when offline
  private readonly queueableTaskIDs = [
    'createJob',
    'createUserWithPassword',
    'editUser',
    'storeCompanyData',
    'storeCompanyLogo',
    'storeUserData'
  ];

  constructor() {
    this.init();
  }

  /**
   * Initialize the service
   */
  private async init(): Promise<void> {
    if (this.initialized) return;

    // Load queue from storage
    await this.loadQueue();

    // Subscribe to network status changes
    this.networkSubscription = this.networkService.onlineStatus$.subscribe(isOnline => {
      if (isOnline && this.queue.length > 0) {
        this.logger.log(`Network online: Processing ${this.queue.length} queued requests`);
        this.processQueue().catch(error => {
          this.errorHandler.handleError('OfflineQueue-Process', error);
        });
      }
    });

    // Process queue if already online
    if (this.networkService.isOnline() && this.queue.length > 0) {
      this.processQueue().catch(error => {
        this.errorHandler.handleError('OfflineQueue-InitProcess', error);
      });
    }

    this.initialized = true;
  }

  /**
   * Load queue from storage
   */
  private async loadQueue(): Promise<void> {
    try {
      const stored = await this.storageService.getLarge<QueuedRequest[]>(this.STORAGE_KEY);
      if (stored && Array.isArray(stored)) {
        // Filter out completed requests (keep only pending, processing, failed)
        this.queue = stored.filter(req => req.status !== 'completed');
        this.updatePendingCount();
        this.logger.log(`Loaded ${this.queue.length} queued requests from storage`);
      }
    } catch (error) {
      this.errorHandler.handleStorageError('loadQueue', this.STORAGE_KEY, error);
      this.queue = [];
    }
  }

  /**
   * Save queue to storage
   */
  private async saveQueue(): Promise<void> {
    try {
      await this.storageService.setLarge(this.STORAGE_KEY, this.queue);
    } catch (error) {
      this.errorHandler.handleStorageError('saveQueue', this.STORAGE_KEY, error);
    }
  }

  /**
   * Check if a taskID should be queued
   */
  private isQueueable(taskID: string): boolean {
    return this.queueableTaskIDs.includes(taskID);
  }

  /**
   * Generate unique ID for queued request
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Serialize FormData to a storable format
   */
  private async serializeFormData(formData: FormData): Promise<SerializedRequestData> {
    const data: { [key: string]: string | number | boolean | SerializedFile } = {};
    
    // Convert FormData to array of entries manually (works with all TypeScript versions)
    const entries: Array<[string, FormDataEntryValue]> = [];
    formData.forEach((value, key) => {
      entries.push([key, value]);
    });
    
    // Process entries sequentially to handle async file conversion
    for (const [key, value] of entries) {
      if (value instanceof File) {
        // Convert File to Base64
        try {
          const base64 = await this.fileToBase64(value);
          data[key] = {
            type: 'file',
            name: value.name,
            data: base64,
            lastModified: value.lastModified
          };
        } catch (error) {
          this.logger.warn(`Failed to serialize file ${value.name}:`, error);
          throw new Error(`Failed to serialize file: ${value.name}`);
        }
      } else {
        data[key] = value;
      }
    }

    return {
      isFormData: true,
      data
    };
  }

  /**
   * Convert File to Base64 string
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (data:image/png;base64,)
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Deserialize FormData from stored format
   */
  private deserializeFormData(serialized: SerializedRequestData): FormData {
    const formData = new FormData();

    for (const [key, value] of Object.entries(serialized.data)) {
      if (typeof value === 'object' && value !== null && 'type' in value && value.type === 'file') {
        const fileData = value as SerializedFile;
        // Convert Base64 back to Blob
        const byteCharacters = atob(fileData.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray]);
        const file = new File([blob], fileData.name, { lastModified: fileData.lastModified || Date.now() });
        formData.append(key, file);
      } else {
        formData.append(key, String(value));
      }
    }

    return formData;
  }

  /**
   * Serialize ApiRequest (plain object) to storable format
   */
  private serializeApiRequest(data: ApiRequest, urlEncodedHeaders?: string): SerializedRequestData {
    return {
      isFormData: false,
      data: data as { [key: string]: string | number | boolean },
      urlEncodedHeaders
    };
  }

  /**
   * Deserialize ApiRequest from stored format
   */
  private deserializeApiRequest(serialized: SerializedRequestData): ApiRequest {
    return serialized.data as ApiRequest;
  }

  /**
   * Queue a request for later processing
   */
  async queueRequest(
    data: ApiRequest | FormData,
    urlEncodedHeaders: string = '',
    metadata?: QueuedRequestMetadata
  ): Promise<string> {
    // Extract taskID
    let taskID: string;
    if (data instanceof FormData) {
      taskID = data.get('taskID') as string || '';
    } else {
      taskID = data.taskID || '';
    }

    if (!taskID || !this.isQueueable(taskID)) {
      throw new Error(`TaskID "${taskID}" is not queueable`);
    }

    // Check queue size limit
    if (this.queue.length >= this.MAX_QUEUE_SIZE) {
      // Remove oldest completed requests
      const completedRequests = this.queue.filter(req => req.status === 'completed');
      if (completedRequests.length > 0) {
        this.queue = this.queue.filter(req => req.status !== 'completed');
      } else {
        // Remove oldest pending request if no completed requests
        this.queue.shift();
      }
    }

    // Serialize request data
    let serializedData: SerializedRequestData;
    if (data instanceof FormData) {
      serializedData = await this.serializeFormData(data);
      serializedData.urlEncodedHeaders = urlEncodedHeaders;
    } else {
      serializedData = this.serializeApiRequest(data, urlEncodedHeaders);
    }

    // Create queued request
    const queuedRequest: QueuedRequest = {
      id: this.generateId(),
      taskID,
      serializedData,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending',
      metadata
    };

    // Add to queue
    this.queue.push(queuedRequest);
    await this.saveQueue();
    this.updatePendingCount();

    this.logger.log(`Queued request: ${taskID} (ID: ${queuedRequest.id})`);

    return queuedRequest.id;
  }

  /**
   * Process all pending requests in the queue
   */
  async processQueue(): Promise<void> {
    if (this.isProcessing) {
      this.logger.log('Queue processing already in progress');
      return;
    }

    if (!this.networkService.isOnline()) {
      this.logger.log('Cannot process queue: Device is offline');
      return;
    }

    const pendingRequests = this.queue.filter(req => req.status === 'pending' || req.status === 'failed');
    if (pendingRequests.length === 0) {
      return;
    }

    this.isProcessing = true;
    this.logger.log(`Processing ${pendingRequests.length} queued requests`);

    let successCount = 0;
    let failureCount = 0;

    // Process requests sequentially to avoid overwhelming the server
    for (const request of pendingRequests) {
      try {
        request.status = 'processing';
        await this.saveQueue();

        // Deserialize request data
        let formData: FormData;
        let urlEncodedHeaders: string | undefined;

        if (request.serializedData.isFormData) {
          formData = this.deserializeFormData(request.serializedData);
          urlEncodedHeaders = request.serializedData.urlEncodedHeaders;
        } else {
          // Convert ApiRequest to FormData (same logic as ApiService)
          const apiRequest = this.deserializeApiRequest(request.serializedData);
          formData = new FormData();
          Object.entries(apiRequest).forEach(([key, value]) => {
            formData.append(key, String(value));
          });
          urlEncodedHeaders = request.serializedData.urlEncodedHeaders;
        }

        // Send request directly using HttpClient (avoid circular dependency with ApiService)
        const headers: { [key: string]: string } = {};
        if (urlEncodedHeaders) {
          headers['Content-Type'] = urlEncodedHeaders;
        }

        const response: ApiResponse = await firstValueFrom(
          this.http.post<any>(this.baseUrl, formData, {
            headers,
            withCredentials: false
          }).pipe(
            catchError((error: HttpErrorResponse) => {
              if (error.error instanceof ErrorEvent) {
                throw new Error(`HTTP-Fehler: ${error.error.message}`);
              } else {
                throw new Error(`HTTP-Fehler ${error.status}: ${error.statusText}`);
              }
            })
          )
        );

        // Check if successful
        if (response.STATUS && response.STATUS.includes('SUCCESS')) {
          request.status = 'completed';
          successCount++;
          this.logger.log(`Queued request completed: ${request.taskID} (ID: ${request.id})`);
        } else {
          throw new Error(`API returned error status: ${response.STATUS}`);
        }
      } catch (error) {
        request.retryCount++;
        request.error = error instanceof Error ? error.message : String(error);

        if (request.retryCount >= this.MAX_RETRIES) {
          request.status = 'failed';
          failureCount++;
          this.logger.warn(`Queued request failed after ${this.MAX_RETRIES} retries: ${request.taskID} (ID: ${request.id})`);
        } else {
          request.status = 'failed'; // Will be retried on next processQueue call
          this.logger.log(`Queued request failed, will retry: ${request.taskID} (ID: ${request.id}, attempt ${request.retryCount}/${this.MAX_RETRIES})`);
          
          // Exponential backoff delay
          const delay = this.RETRY_DELAY_BASE * Math.pow(2, request.retryCount - 1);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      await this.saveQueue();
      this.updatePendingCount();
    }

    this.isProcessing = false;

    if (successCount > 0) {
      this.queueProcessedSubject.next(successCount);
    }

    // Show toast notification
    if (successCount > 0) {
      const message = failureCount > 0
        ? `${successCount} Request(s) erfolgreich synchronisiert. ${failureCount} fehlgeschlagen.`
        : `${successCount} Request(s) erfolgreich synchronisiert.`;
      this.toastService.showSuccess(message, 4000);
    } else if (failureCount > 0) {
      this.toastService.showError(`${failureCount} Request(s) konnten nicht synchronisiert werden.`, 5000);
    }

    // Clean up completed requests (keep last 10 for reference)
    const completedRequests = this.queue.filter(req => req.status === 'completed');
    if (completedRequests.length > 10) {
      const toRemove = completedRequests.slice(0, completedRequests.length - 10);
      this.queue = this.queue.filter(req => !toRemove.includes(req));
      await this.saveQueue();
    }
  }

  /**
   * Get number of pending requests
   */
  getPendingCount(): number {
    return this.queue.filter(req => req.status === 'pending' || req.status === 'failed').length;
  }

  /**
   * Get all pending requests
   */
  getPendingRequests(): QueuedRequest[] {
    return this.queue.filter(req => req.status === 'pending' || req.status === 'failed');
  }

  /**
   * Get all requests (for UI display)
   */
  getAllRequests(): QueuedRequest[] {
    return [...this.queue];
  }

  /**
   * Retry a specific request
   */
  async retryRequest(id: string): Promise<boolean> {
    const request = this.queue.find(req => req.id === id);
    if (!request) {
      return false;
    }

    request.status = 'pending';
    request.retryCount = 0;
    request.error = undefined;
    await this.saveQueue();
    this.updatePendingCount();

    // Process immediately if online
    if (this.networkService.isOnline()) {
      await this.processQueue();
    }

    return true;
  }

  /**
   * Remove a request from the queue
   */
  async removeRequest(id: string): Promise<boolean> {
    const index = this.queue.findIndex(req => req.id === id);
    if (index === -1) {
      return false;
    }

    this.queue.splice(index, 1);
    await this.saveQueue();
    this.updatePendingCount();
    return true;
  }

  /**
   * Clear all completed requests
   */
  async clearCompleted(): Promise<void> {
    const beforeCount = this.queue.length;
    this.queue = this.queue.filter(req => req.status !== 'completed');
    await this.saveQueue();
    this.updatePendingCount();
    this.logger.log(`Cleared ${beforeCount - this.queue.length} completed requests`);
  }

  /**
   * Update pending count observable
   */
  private updatePendingCount(): void {
    const count = this.getPendingCount();
    this.pendingCountSubject.next(count);
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
    }
  }
}
